import React, { useState, useEffect } from 'react';
import {
    Table, Button, Modal, Form, Input,
    Space, message, Popconfirm, Spin, Tooltip
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, ExpandOutlined, SettingOutlined, FileTextOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../services/agentService';

const { TextArea } = Input;

const SettingsPage = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState({ key: '', value: '' });
    const [editingSetting, setEditingSetting] = useState(null);
    const [form] = Form.useForm();

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/settings');
            setSettings(response.data);
        } catch (err) {
            message.error('Ayarlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleAdd = () => {
        setEditingSetting(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingSetting(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (key) => {
        try {
            await apiClient.delete(`/settings/${key}`);
            message.success('Ayar silindi');
            fetchSettings();
        } catch (err) {
            message.error('Silme hatası');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await apiClient.post('/settings', values);
            message.success(editingSetting ? 'Ayar güncellendi' : 'Ayar eklendi');
            setModalVisible(false);
            fetchSettings();
        } catch (err) {
            if (err.errorFields) return;
            message.error('İşlem hatası');
        }
    };

    const handleCopyValue = (value) => {
        navigator.clipboard.writeText(value);
        message.success('Değer panoya kopyalandı');
    };

    const handlePreview = (record) => {
        setPreviewContent({ key: record.settingKey, value: record.settingValue });
        setPreviewModalVisible(true);
    };

    const columns = [
        {
            title: 'Anahtar',
            dataIndex: 'settingKey',
            key: 'settingKey',
            width: 200,
            render: (key) => (
                <span className="admin-tag admin-tag-purple">{key}</span>
            ),
        },
        {
            title: 'Değer',
            dataIndex: 'settingValue',
            key: 'settingValue',
            render: (value, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        color: 'var(--admin-text-secondary)',
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                    }}>
                        {value?.substring(0, 100)}{value?.length > 100 ? '...' : ''}
                    </span>
                    {value?.length > 100 && (
                        <Tooltip title="Tam içeriği görüntüle">
                            <Button
                                type="text"
                                size="small"
                                icon={<ExpandOutlined />}
                                onClick={() => handlePreview(record)}
                                style={{ color: 'var(--admin-accent-primary)' }}
                            />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: 'Açıklama',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 250,
            render: (desc) => (
                <span style={{ color: 'var(--admin-text-tertiary)', fontSize: 13 }}>
                    {desc || '-'}
                </span>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 150,
            render: (_, record) => (
                <Space size={8}>
                    <Tooltip title="Değeri kopyala">
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon"
                                icon={<CopyOutlined />}
                                onClick={() => handleCopyValue(record.settingValue)}
                            />
                        </motion.div>
                    </Tooltip>
                    <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                        <Button
                            className="admin-btn-icon"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </motion.div>
                    <Popconfirm
                        title="Bu ayarı silmek istediğinize emin misiniz?"
                        onConfirm={() => handleDelete(record.settingKey)}
                        okText="Evet"
                        cancelText="Hayır"
                        okButtonProps={{ danger: true }}
                    >
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon danger"
                                icon={<DeleteOutlined />}
                            />
                        </motion.div>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Ayarlar</h1>
                    <p className="admin-page-subtitle">
                        Sistem ayarlarını yönetin. KVKK metni ve diğer konfigürasyonlar burada.
                    </p>
                </div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                        className="admin-btn-primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        Yeni Ayar
                    </Button>
                </motion.div>
            </div>

            {/* Table */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        scroll={{ x: 'max-content' }}
                        columns={columns}
                        dataSource={settings}
                        rowKey="id"
                        pagination={false}
                    />
                )}
            </motion.div>

            {/* Edit/Add Modal */}
            <AnimatePresence>
                {modalVisible && (
                    <Modal
                        title={
                            <div className="admin-modal-heading">
                                <div className="admin-modal-heading-icon">
                                    <SettingOutlined />
                                </div>
                                <div>
                                    <div className="admin-modal-heading-title">
                                        {editingSetting ? 'Ayarı Düzenle' : 'Yeni Ayar Ekle'}
                                    </div>
                                    <div className="admin-modal-heading-subtitle">
                                        Anahtar-değer yapısındaki sistem ayarını tanımlayın
                                    </div>
                                </div>
                            </div>
                        }
                        open={modalVisible}
                        onOk={handleSubmit}
                        onCancel={() => setModalVisible(false)}
                        okText={editingSetting ? 'Güncelle' : 'Ekle'}
                        cancelText="İptal"
                        width="92vw"
                        style={{ maxWidth: 600 }}
                        className="admin-modal"
                        rootClassName="admin-modal"
                        okButtonProps={{ className: 'admin-btn-primary' }}
                        cancelButtonProps={{ className: 'admin-btn-secondary' }}
                    >
                        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item
                                name="settingKey"
                                label="Anahtar (Key)"
                                rules={[{ required: true, message: 'Anahtar gerekli' }]}
                            >
                                <Input
                                    placeholder="Örnek: KVKK_TEXT"
                                    disabled={!!editingSetting}
                                />
                            </Form.Item>

                            <Form.Item
                                name="settingValue"
                                label="Değer"
                                rules={[{ required: true, message: 'Değer gerekli' }]}
                            >
                                <TextArea
                                    rows={8}
                                    placeholder="Ayar değeri"
                                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                                />
                            </Form.Item>

                            <Form.Item name="description" label="Açıklama">
                                <Input placeholder="Bu ayarın ne işe yaradığı (opsiyonel)" />
                            </Form.Item>
                        </Form>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Preview Modal */}
            <Modal
                title={
                    <div className="admin-modal-heading">
                        <div className="admin-modal-heading-icon">
                            <FileTextOutlined />
                        </div>
                        <div>
                            <div className="admin-modal-heading-title">
                                <span className="admin-tag admin-tag-purple">{previewContent.key}</span>
                            </div>
                            <div className="admin-modal-heading-subtitle">
                                Ayar değerinin tam görünümü
                            </div>
                        </div>
                    </div>
                }
                open={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button
                        key="copy"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyValue(previewContent.value)}
                        className="admin-btn-secondary"
                    >
                        Kopyala
                    </Button>,
                    <Button
                        key="close"
                        onClick={() => setPreviewModalVisible(false)}
                        className="admin-btn-primary"
                    >
                        Kapat
                    </Button>,
                ]}
                width="92vw"
                style={{ maxWidth: 800 }}
                className="admin-modal"
                rootClassName="admin-modal"
            >
                <div style={{
                    background: 'var(--admin-bg-tertiary)',
                    borderRadius: 'var(--admin-radius-md)',
                    padding: 20,
                    maxHeight: 500,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: 'var(--admin-text-secondary)',
                }}>
                    {previewContent.value}
                </div>
            </Modal>
        </div>
    );
};

export default SettingsPage;
